/**
 * ESG SaaS Platform - Emissions API Service
 *
 * API service for GHG emissions data (Scope 1, 2, 3).
 * Updated to match the actual backend API structure with separate scope endpoints.
 */

import { api } from './api';

// Types
export type EmissionScope = 'scope_1' | 'scope_2' | 'scope_3';

// Scope 1 Types
export interface Scope1EntryCreate {
  category: string;  // 'Stationary Combustion' | 'Mobile Combustion' | 'Fugitive Emissions' | 'Process Emissions'
  subcategory: string;
  activity?: string;  // Activity type (e.g., 'gaseous_fuels', 'on_road') for emission factor lookup
  fuel_type?: string;
  activity_amount: number;
  activity_unit: string;
  emission_factor_value?: number;  // User-provided emission factor (when custom or pre-selected)
  emission_factor_source?: string;  // Source of the factor (Custom, DEFRA, EPA, etc.)
  facility_id: string;
  year: number;
  month: number;
  reporting_period?: string;
  equipment_id?: string;
  data_source?: string;
  data_quality?: 'primary' | 'secondary' | 'tertiary';
  notes?: string;
  attachment_ids?: string[];
}

export interface Scope1Entry {
  id: string;
  category: string;
  subcategory: string;
  activity?: string;  // Activity type (e.g., 'gaseous_fuels', 'refrigerant_leakage')
  fuel_type?: string;
  activity_amount: number;
  activity_unit: string;
  emissions_value: number;
  emissions_unit: string;
  emission_factor: number;
  emission_factor_source: string;
  facility_id: string;
  facility_name: string;
  year: number;
  month: number;
  reporting_period: string;
  data_source: string;
  data_quality: string;
  notes?: string;  // Notes field
  status: string;
  created_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
  attachment_count?: number;
}

// Scope 2 Types
export interface Scope2EntryCreate {
  category: string;  // 'Purchased Electricity' | 'Purchased Heat' | 'Purchased Steam' | 'Purchased Cooling'
  method: 'Location-Based' | 'Market-Based';
  energy_source: string;
  consumption_amount: number;
  consumption_unit?: string;
  emission_factor_value?: number;  // User-provided emission factor (when custom or pre-selected)
  emission_factor_source?: string;  // Source of the factor (Custom, DEFRA, IEA, etc.)
  renewable_share?: number;
  grid_region: string;
  facility_id: string;
  year: number;
  month: number;
  reporting_period?: string;
  supplier?: string;
  contract_type?: string;
  data_source?: string;
  data_quality?: 'primary' | 'secondary' | 'tertiary';
  notes?: string;
  attachment_ids?: string[];
}

export interface Scope2Entry {
  id: string;
  category: string;
  method: string;
  energy_source: string;
  consumption_amount: number;
  consumption_unit: string;
  consumption_mwh: number;
  renewable_share: number;
  emissions_value: number;
  emissions_unit: string;
  emission_factor: number;
  emission_factor_source: string;
  grid_region: string;
  facility_id: string;
  facility_name: string;
  year: number;
  month: number;
  reporting_period: string;
  data_source: string;
  data_quality: string;
  notes?: string;  // Notes field
  status: string;
  created_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
  attachment_count?: number;
}

// Unified EmissionEntry for frontend display (combines all scopes)
export interface EmissionEntry {
  id: string;
  scope: string;
  category: string;
  activity?: string;  // Activity type key (e.g., 'gaseous_fuels', 'refrigerant_leakage')
  activity_type: string;  // Display value (subcategory/fuel type for display)
  fuel_type?: string;
  activity_data: number;
  activity_unit: string;
  // Scope 2 specific fields
  method?: string;  // 'Location-Based' | 'Market-Based'
  renewable_share?: number;
  grid_region?: string;
  emissions_kg_co2e: number;
  emission_factor_value?: number;
  emission_factor_source?: string;  // Source of emission factor (Custom, DEFRA, etc.)
  facility_id: string;
  facility_name: string;
  year: number;
  month: number;
  reporting_period: string;
  data_source?: string;
  notes?: string;  // Notes field
  status: string;
  created_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
  attachment_count?: number;
}

// Generic create type for frontend forms
export interface EmissionEntryCreate {
  scope: EmissionScope;
  category: string;
  activity_type: string;
  activity_data: number;
  activity_unit: string;
  facility_id: string;
  year: number;
  month: number;
  reporting_period?: string;
  data_source?: string;
  notes?: string;
}

export interface EmissionsListResponse {
  entries: EmissionEntry[];
  total: number;
  limit: number;
  offset: number;
  total_emissions: number;
  approved_count: number;
  pending_count: number;
}

export interface EmissionsSummary {
  period: string;
  total_emissions: number;
  scope1_total: number;
  scope2_location_based: number;
  scope2_market_based: number;
  scope3_total: number;
  by_scope: {
    scope1: { value: number; percentage: number };
    scope2: { value: number; percentage: number };
    scope3: { value: number; percentage: number };
  };
  data_completeness: {
    scope1: number;
    scope2: number;
    scope3: number;
    overall: number;
  };
  entry_counts: {
    scope1: number;
    scope2: number;
    scope3: number;
    total: number;
  };
}

// Query parameters
interface ListParams {
  period?: string;
  facility?: string;
  category?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

// Helper to convert backend response to unified EmissionEntry
function toEmissionEntry(entry: Scope1Entry | Scope2Entry, scope: string): EmissionEntry {
  const isScope1 = 'subcategory' in entry;
  const scope2Entry = !isScope1 ? (entry as Scope2Entry) : null;
  return {
    id: entry.id,
    scope,
    category: entry.category,
    activity: isScope1 ? (entry as Scope1Entry).activity : undefined,  // Activity type key for form population
    activity_type: isScope1 ? (entry as Scope1Entry).subcategory : (entry as Scope2Entry).energy_source,
    fuel_type: isScope1 ? (entry as Scope1Entry).fuel_type : undefined,
    activity_data: isScope1 ? (entry as Scope1Entry).activity_amount : (entry as Scope2Entry).consumption_amount,
    activity_unit: isScope1 ? (entry as Scope1Entry).activity_unit : (entry as Scope2Entry).consumption_unit,
    // Scope 2 specific fields
    method: scope2Entry?.method,
    renewable_share: scope2Entry?.renewable_share,
    grid_region: scope2Entry?.grid_region,
    emissions_kg_co2e: entry.emissions_value * 1000, // Convert tCO2e to kg
    emission_factor_value: entry.emission_factor,
    emission_factor_source: entry.emission_factor_source,  // Include emission factor source
    facility_id: entry.facility_id,
    facility_name: entry.facility_name,
    year: entry.year,
    month: entry.month,
    reporting_period: entry.reporting_period,
    data_source: entry.data_source,
    notes: entry.notes,  // Include notes
    status: entry.status,
    created_by: entry.created_by,
    created_at: entry.created_at,
    approved_by: entry.approved_by,
    approved_at: entry.approved_at,
    attachment_count: entry.attachment_count || 0,
  };
}

// Build query string from params
function buildQuery(params: ListParams): string {
  const query = new URLSearchParams();
  if (params.period) query.append('period', params.period);
  if (params.facility) query.append('facility', params.facility);
  if (params.category) query.append('category', params.category);
  if (params.status) query.append('status', params.status);
  if (params.limit) query.append('limit', params.limit.toString());
  if (params.offset) query.append('offset', params.offset.toString());
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
}

// Service functions
export const emissionsService = {
  // ================== SCOPE 1 OPERATIONS ==================
  createScope1Entry: (entry: Scope1EntryCreate) =>
    api.post<Scope1Entry>('/emissions/scope1/entries', entry),

  listScope1Entries: async (params: ListParams = {}): Promise<{ entries: Scope1Entry[]; total: number; total_emissions: number; approved_count: number; pending_count: number }> => {
    const response = await api.get<{ entries: Scope1Entry[]; total: number; total_emissions: number; approved_count: number; pending_count: number }>(
      `/emissions/scope1/entries${buildQuery(params)}`
    );
    return response;
  },

  getScope1Entry: (entryId: string) =>
    api.get<Scope1Entry>(`/emissions/scope1/entries/${entryId}`),

  updateScope1Entry: (entryId: string, entry: Scope1EntryCreate) =>
    api.put<{ id: string; updated: boolean }>(`/emissions/scope1/entries/${entryId}`, entry),

  deleteScope1Entry: (entryId: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/emissions/scope1/entries/${entryId}`),

  approveScope1Entry: (entryId: string, comment?: string) =>
    api.post<{ id: string; status: string }>(`/emissions/scope1/entries/${entryId}/approve`, { comment }),

  rejectScope1Entry: (entryId: string, reason: string) =>
    api.post<{ id: string; status: string; reason: string }>(`/emissions/scope1/entries/${entryId}/reject`, { reason }),

  // ================== SCOPE 2 OPERATIONS ==================
  createScope2Entry: (entry: Scope2EntryCreate) =>
    api.post<Scope2Entry>('/emissions/scope2/entries', entry),

  listScope2Entries: async (params: ListParams = {}): Promise<{ entries: Scope2Entry[]; total: number; location_based_total: number; market_based_total: number; approved_count: number; pending_count: number }> => {
    const response = await api.get<{ entries: Scope2Entry[]; total: number; location_based_total: number; market_based_total: number; approved_count: number; pending_count: number }>(
      `/emissions/scope2/entries${buildQuery(params)}`
    );
    return response;
  },

  getScope2Entry: (entryId: string) =>
    api.get<Scope2Entry>(`/emissions/scope2/entries/${entryId}`),

  updateScope2Entry: (entryId: string, entry: Scope2EntryCreate) =>
    api.put<{ id: string; updated: boolean }>(`/emissions/scope2/entries/${entryId}`, entry),

  deleteScope2Entry: (entryId: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/emissions/scope2/entries/${entryId}`),

  approveScope2Entry: (entryId: string, comment?: string) =>
    api.post<{ id: string; status: string }>(`/emissions/scope2/entries/${entryId}/approve`, { comment }),

  rejectScope2Entry: (entryId: string, reason: string) =>
    api.post<{ id: string; status: string; reason: string }>(`/emissions/scope2/entries/${entryId}/reject`, { reason }),

  // ================== UNIFIED OPERATIONS (for convenience) ==================

  // Map frontend Scope 2 category to backend enum
  // Frontend uses: 'Electricity', 'Heat', 'Steam', 'Cooling'
  // Backend expects: 'Purchased Electricity', 'Purchased Heat', 'Purchased Steam', 'Purchased Cooling'

  // Create entry - routes to correct scope endpoint
  createEntry: async (entry: EmissionEntryCreate): Promise<EmissionEntry> => {
    if (entry.scope === 'scope_1') {
      const scope1Entry: Scope1EntryCreate = {
        category: entry.category,
        subcategory: entry.activity_type,
        activity_amount: entry.activity_data,
        activity_unit: entry.activity_unit,
        facility_id: entry.facility_id,
        year: entry.year,
        month: entry.month,
        data_source: entry.data_source,
        notes: entry.notes,
      };
      const result = await emissionsService.createScope1Entry(scope1Entry);
      return toEmissionEntry(result, 'scope_1');
    } else if (entry.scope === 'scope_2') {
      // Map frontend category names to backend enum values
      const categoryMap: Record<string, string> = {
        'Electricity': 'Purchased Electricity',
        'Heat': 'Purchased Heat',
        'Steam': 'Purchased Steam',
        'Cooling': 'Purchased Cooling',
        // Also accept full names in case they're already correct
        'Purchased Electricity': 'Purchased Electricity',
        'Purchased Heat': 'Purchased Heat',
        'Purchased Steam': 'Purchased Steam',
        'Purchased Cooling': 'Purchased Cooling',
      };

      const backendCategory = categoryMap[entry.category] || 'Purchased Electricity';

      const scope2Entry: Scope2EntryCreate = {
        category: backendCategory,
        method: 'Location-Based',
        energy_source: entry.activity_type || entry.category,
        consumption_amount: entry.activity_data,
        consumption_unit: entry.activity_unit || 'kWh',
        grid_region: 'DE',
        facility_id: entry.facility_id,
        year: entry.year,
        month: entry.month,
        data_source: entry.data_source || 'measured',
        notes: entry.notes,
      };
      const result = await emissionsService.createScope2Entry(scope2Entry);
      return toEmissionEntry(result, 'scope_2');
    }
    throw new Error('Unsupported scope for entry creation');
  },

  // List entries from both scopes combined
  listEntries: async (params: ListParams & { scope?: EmissionScope } = {}): Promise<EmissionsListResponse> => {
    const queryParams = {
      period: params.period,
      facility: params.facility,
      category: params.category,
      status: params.status,
      limit: params.limit || 50,
      offset: params.offset || 0,
    };

    // Fetch both scopes in parallel
    const [scope1Response, scope2Response] = await Promise.all([
      emissionsService.listScope1Entries(queryParams),
      emissionsService.listScope2Entries(queryParams),
    ]);

    // Convert and combine entries
    const scope1Entries = scope1Response.entries.map(e => toEmissionEntry(e, 'scope_1'));
    const scope2Entries = scope2Response.entries.map(e => toEmissionEntry(e, 'scope_2'));

    // Filter by scope if specified
    let allEntries = [...scope1Entries, ...scope2Entries];
    if (params.scope === 'scope_1') {
      allEntries = scope1Entries;
    } else if (params.scope === 'scope_2') {
      allEntries = scope2Entries;
    }

    // Sort by created_at descending
    allEntries.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    const totalEmissions = (scope1Response.total_emissions || 0) * 1000 +
                          ((scope2Response.location_based_total || 0) + (scope2Response.market_based_total || 0)) * 1000;

    return {
      entries: allEntries,
      total: scope1Response.total + scope2Response.total,
      limit: queryParams.limit,
      offset: queryParams.offset,
      total_emissions: totalEmissions,
      approved_count: scope1Response.approved_count + scope2Response.approved_count,
      pending_count: scope1Response.pending_count + scope2Response.pending_count,
    };
  },

  // Delete entry - determines scope from ID prefix or requires scope
  deleteEntry: async (entryId: string, scope?: EmissionScope): Promise<{ id: string; deleted: boolean }> => {
    // Try both endpoints if scope not specified
    if (!scope) {
      try {
        return await emissionsService.deleteScope1Entry(entryId);
      } catch {
        return await emissionsService.deleteScope2Entry(entryId);
      }
    }
    if (scope === 'scope_1') {
      return emissionsService.deleteScope1Entry(entryId);
    }
    return emissionsService.deleteScope2Entry(entryId);
  },

  // Approve entry
  approveEntry: async (entryId: string, comment?: string, scope?: EmissionScope): Promise<{ id: string; status: string }> => {
    if (!scope) {
      try {
        return await emissionsService.approveScope1Entry(entryId, comment);
      } catch {
        return await emissionsService.approveScope2Entry(entryId, comment);
      }
    }
    if (scope === 'scope_1') {
      return emissionsService.approveScope1Entry(entryId, comment);
    }
    return emissionsService.approveScope2Entry(entryId, comment);
  },

  // Reject entry
  rejectEntry: async (entryId: string, reason: string, scope?: EmissionScope): Promise<{ id: string; status: string; reason: string }> => {
    if (!scope) {
      try {
        return await emissionsService.rejectScope1Entry(entryId, reason);
      } catch {
        return await emissionsService.rejectScope2Entry(entryId, reason);
      }
    }
    if (scope === 'scope_1') {
      return emissionsService.rejectScope1Entry(entryId, reason);
    }
    return emissionsService.rejectScope2Entry(entryId, reason);
  },

  // ================== ANALYTICS ==================

  // Get summary - matches backend /emissions/summary
  getSummary: (period?: string, facilityId?: string) => {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (facilityId) params.append('facility_id', facilityId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get<EmissionsSummary>(`/emissions/summary${query}`);
  },

  // Get trends - matches backend /emissions/trends
  getTrends: (startPeriod?: string, endPeriod?: string) => {
    const query = new URLSearchParams();
    if (startPeriod) query.append('start_period', startPeriod);
    if (endPeriod) query.append('end_period', endPeriod);
    const queryString = query.toString();
    return api.get<{ periods: string[]; data: { period: string; scope1: number; scope2: number; scope3: number; total: number }[] }>(
      `/emissions/trends${queryString ? `?${queryString}` : ''}`
    );
  },

  // Get monthly emissions data for a specific year - for dashboard charts
  getMonthlyEmissions: (year: number, facilityId?: string) => {
    const params = new URLSearchParams({ year: year.toString() });
    if (facilityId) params.append('facility_id', facilityId);
    return api.get<{
      year: number;
      monthly_emissions: {
        labels: string[];
        scope1: number[];
        scope2: number[];
        scope3: number[];
        total: number[];
      };
      current_month: {
        month: string;
        monthIndex: number;
        scope1: number;
        scope2: number;
        scope3: number;
        total: number;
        vsLastMonth: number;
      };
    }>(`/emissions/monthly?${params.toString()}`);
  },

  // Get KPIs - matches backend /emissions/kpis
  getKPIs: () =>
    api.get<{ kpis: { id: string; title: string; value: number; unit: string; change: number; status: string }[] }>('/emissions/kpis'),

  // ================== EMISSION FACTORS ==================
  getEmissionFactors: (activity?: string, region?: string) => {
    const query = new URLSearchParams();
    if (activity) query.append('activity', activity);
    if (region) query.append('region', region);
    const queryString = query.toString();
    return api.get<{ factors: { activity: string; region: string; value: number; unit: string; source: string }[]; count: number }>(
      `/emissions/emission-factors${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Export emissions data as CSV or Excel file (Admin only).
   */
  exportData: async (format: 'csv' | 'xlsx' = 'csv', period?: string, scope?: number): Promise<void> => {
    const query = new URLSearchParams({ format });
    if (period) query.append('period', period);
    if (scope) query.append('scope', scope.toString());

    const response = await api.getBlob(`/emissions/export?${query.toString()}`);
    const url = URL.createObjectURL(response.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = response.filename || `emissions_export.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
};

// ================== ATTACHMENT TYPES ==================
export interface Attachment {
  id: string;
  original_filename: string;
  file_size_bytes: number;
  file_type: string;
  uploaded_by: string;
  uploaded_at: string;
  emission_entry_id?: string;
}

export interface AttachmentUploadResponse {
  attachments: Attachment[];
  total_uploaded: number;
  total_size_bytes: number;
}

// ================== ATTACHMENT SERVICE ==================
export const attachmentService = {
  /**
   * Upload multiple files as evidence attachments.
   * Files are stored in audit-friendly folder structure by year/month/scope.
   */
  uploadFiles: async (
    files: File[],
    year: number,
    month: number,
    scope: number
  ): Promise<Attachment[]> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await api.postFormData<AttachmentUploadResponse>(
      `/attachments/upload?year=${year}&month=${month}&scope=${scope}`,
      formData
    );
    return response.attachments;
  },

  /**
   * Download a single attachment by ID.
   */
  downloadAttachment: async (attachmentId: string): Promise<{ url: string; filename: string }> => {
    const response = await api.getBlob(`/attachments/${attachmentId}/download`);
    const url = URL.createObjectURL(response.blob);
    return { url, filename: response.filename || 'download' };
  },

  /**
   * Delete an attachment.
   * Before approval: Contributors can delete their own attachments.
   * After approval: Only admins can delete.
   */
  deleteAttachment: (attachmentId: string) =>
    api.delete<{ deleted: boolean; id: string }>(`/attachments/${attachmentId}`),

  /**
   * List all attachments for a specific emission entry.
   */
  listEntryAttachments: (entryId: string) =>
    api.get<Attachment[]>(`/attachments/entry/${entryId}`),

  /**
   * Link an uploaded attachment to an emission entry.
   */
  linkAttachmentToEntry: (attachmentId: string, entryId: string) =>
    api.post<Attachment>(`/attachments/${attachmentId}/link/${entryId}`, {}),

  /**
   * Export all evidence files as a ZIP archive for audit purposes.
   * Admin only.
   */
  exportAuditEvidence: async (
    year: number,
    month?: number,
    scope?: number
  ): Promise<{ url: string; filename: string }> => {
    const query = new URLSearchParams();
    query.append('year', year.toString());
    if (month) query.append('month', month.toString());
    if (scope) query.append('scope', scope.toString());

    const response = await api.getBlob(`/attachments/audit-export?${query.toString()}`);
    const url = URL.createObjectURL(response.blob);
    return { url, filename: response.filename || `evidence_export_${year}.zip` };
  },
};
